import { useQuery } from "@apollo/client";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { ReactNode, useMemo, useState } from "react";

import { MANIFEST_SHIPMENT_LINK_QUERY } from "./api";
import { BillOfLadingReviewDialog } from "./BillOfLadingReviewDialog";
import { getDocumentState } from "./documentStateDisplay";
import { BillSummaryInput, buildBillOfLadingSummaryItems } from "./sections/billOfLadingSummary";
import { SummaryItem } from "./sections/summaryStatus";

interface Options {
  isBillOfLading: boolean;
  /** Whether the bill's review dialog applies to this bill and tenant. */
  canReviewBill: boolean;
  tenantId: string | undefined;
  processedManifestId: string | undefined;
  facilityId: string | null | undefined;
  sourceDocumentId: string | null | undefined;
  sourceDocument: BillSummaryInput["sourceDocument"];
  awaitingRecompute: boolean;
  onViewDocument?: () => void;
  onReviewSaved: () => void;
  /** Called after an unconfirmed bill's upload is discarded from the review dialog. */
  onDiscarded: () => void;
}

/**
 * The bill of lading Summary lines, plus the review dialog their "Review
 * document data" action opens. The shipment lookup is the one the drawer's
 * shipment card already runs (same query and variables), so Apollo serves both
 * from one request.
 */
export function useBillOfLadingSummaryItems({
  isBillOfLading,
  canReviewBill,
  tenantId,
  processedManifestId,
  facilityId,
  sourceDocumentId,
  sourceDocument,
  awaitingRecompute,
  onViewDocument,
  onReviewSaved,
  onDiscarded,
}: Options): {
  items: SummaryItem[];
  reviewDialog: ReactNode;
  /** Opens the review dialog; undefined when the viewer cannot review this bill. */
  onReviewBill: (() => void) | undefined;
} {
  const { featureFlags } = useFeatureFlags();
  const { hasPermissions } = useAuthorization();
  const [reviewOpen, setReviewOpen] = useState(false);
  const containerTracking = !!featureFlags[FeatureFlagKey.ContainerTracking];
  const { data, loading } = useQuery(MANIFEST_SHIPMENT_LINK_QUERY, {
    variables: { search: `tenantId:${tenantId} id:${processedManifestId}` },
    skip: !isBillOfLading || !containerTracking || !tenantId || !processedManifestId,
  });
  const loaded = !loading || data != null;
  const linked = data?.processedManifests?.items?.[0]?.linkedShipment;
  const canReview =
    canReviewBill && !!sourceDocumentId && hasPermissions([Permission.WriteDocument]);

  const onReviewBill = useMemo(
    () => (canReview ? () => setReviewOpen(true) : undefined),
    [canReview]
  );

  const items = useMemo(() => {
    if (!isBillOfLading) return [];
    const link = linked
      ? {
          id: linked.id,
          manifestRequestNumber: linked.manifestRequestNumber,
          reconciliationStatus: linked.reconciliation.status,
        }
      : null;
    return buildBillOfLadingSummaryItems({
      sourceDocument,
      awaitingRecompute,
      onViewDocument,
      shipment: containerTracking && loaded ? { tenantId, link } : null,
    });
  }, [
    isBillOfLading,
    linked,
    containerTracking,
    loaded,
    tenantId,
    sourceDocument,
    awaitingRecompute,
    onViewDocument,
  ]);

  const reviewDialog =
    reviewOpen && sourceDocumentId ? (
      <BillOfLadingReviewDialog
        documentIds={[sourceDocumentId]}
        facilityId={facilityId ?? ""}
        tenantId={tenantId}
        onClose={() => setReviewOpen(false)}
        onSaved={() => {
          setReviewOpen(false);
          onReviewSaved();
        }}
        onDiscarded={
          getDocumentState({ sourceDocument }) === "needsReview"
            ? () => {
                setReviewOpen(false);
                onDiscarded();
              }
            : undefined
        }
      />
    ) : null;

  return { items, reviewDialog, onReviewBill };
}
